import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Input, Modal, Select, Spin, Switch, Tag, message } from "antd";
import { ReloadOutlined, SendOutlined } from "@ant-design/icons";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import {
  getFtcMailOffices,
  getFtcReportPreview,
  getFtcReportSchedule,
  saveFtcReportSchedule,
  sendFtcReport,
} from "./controller";
import "./ftcReports.css";

/**
 * The value report an FTC sends a clinic: what we did, how fast, how much of it
 * needed a person.
 *
 * The preview is the whole point of this page. A report is an outward-facing
 * claim about work performed, so the FTC sees the exact email — and the exact
 * recipient list — before anything leaves. Server-rendered rather than rebuilt
 * here, so what is previewed IS what is sent; a second implementation in JSX
 * would drift from the one Outlook receives.
 */

const PERIODS = [
  { value: "monthly", label: "This month" },
  { value: "weekly", label: "This week" },
];

export default function FtcReportsPage() {
  const [loading, setLoading] = useState(true);
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState(null);
  const [period, setPeriod] = useState("monthly");

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(
    async function (oid, per) {
      if (!oid) return;
      setPreviewing(true);
      const [p, s] = await Promise.all([
        getFtcReportPreview(oid, per),
        getFtcReportSchedule(oid),
      ]);
      setPreview(p.status >= 200 && p.status < 300 ? p.json : null);
      setSchedule(s.status >= 200 && s.status < 300 ? s.json : null);
      setPreviewing(false);
    },
    [],
  );

  useEffect(function () {
    let cancelled = false;
    (async function () {
      const res = await getFtcMailOffices();
      if (cancelled) return;
      const list = (res.json && res.json.offices) || [];
      setOffices(list);
      setLoading(false);
      if (list.length) {
        setOfficeId(list[0].id);
        load(list[0].id, "monthly");
      }
    })();
    return function () { cancelled = true; };
  }, [load]);

  async function patchSchedule(patch) {
    const res = await saveFtcReportSchedule(Object.assign({ office_id: officeId }, patch));
    if (res.status >= 200 && res.status < 300) {
      setSchedule(res.json);
    } else {
      message.error((res.json && res.json.detail) || "Couldn't save the schedule.");
    }
  }

  function confirmSend() {
    const to = (preview && preview.recipients) || [];
    Modal.confirm({
      title: "Send this report?",
      // Naming the addresses in the confirmation, not just a count: this is the
      // last moment before a customer receives a claim about our own performance.
      content: (
        <div>
          <p style={{ marginBottom: 6 }}>It will be sent to:</p>
          <ul className="ftcrep-tolist">
            {to.map((r) => <li key={r.email}>{r.name ? `${r.name} · ${r.email}` : r.email}</li>)}
          </ul>
        </div>
      ),
      okText: "Send",
      onOk: doSend,
    });
  }

  async function doSend() {
    setSending(true);
    try {
      const res = await sendFtcReport({
        office_id: officeId, period, intro: (schedule && schedule.intro) || "",
      });
      if (res.status >= 200 && res.status < 300) {
        message.success("Report sent.");
        load(officeId, period);
      } else {
        message.error((res.json && res.json.detail) || "Couldn't send the report.");
      }
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return <DashboardLayout><div className="ftcrep-center"><Spin /></div></DashboardLayout>;
  }

  if (!offices.length) {
    return (
      <DashboardLayout>
        <div className="ftcrep-center">
          <Empty description="No clinics are assigned to you yet. Ask an admin to assign one." />
        </div>
      </DashboardLayout>
    );
  }

  const recipients = (preview && preview.recipients) || [];

  return (
    <DashboardLayout>
      <div className="ftcrep">
        <div className="ftcrep-head">
          <div>
            <h2 className="ftcrep-title">Reports</h2>
            <p className="ftcrep-sub">What your clinics see about the work we've done.</p>
          </div>
          <div className="ftcrep-head-right">
            <Select
              value={officeId}
              onChange={(v) => { setOfficeId(v); load(v, period); }}
              options={offices.map((o) => ({ value: o.id, label: o.name }))}
              style={{ width: 220 }}
            />
            <Select
              value={period}
              onChange={(v) => { setPeriod(v); load(officeId, v); }}
              options={PERIODS}
              style={{ width: 150 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => load(officeId, period)} />
          </div>
        </div>

        <div className="ftcrep-body">
          <div className="ftcrep-preview">
            {previewing ? (
              <div className="ftcrep-center"><Spin /></div>
            ) : !preview ? (
              <div className="ftcrep-center">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                       description="Couldn't build a preview for this clinic." />
              </div>
            ) : (
              <>
                <div className="ftcrep-subject">{preview.subject}</div>
                {/* The server's own HTML, so the preview cannot drift from the send. */}
                <div className="ftcrep-html"
                     dangerouslySetInnerHTML={{ __html: preview.html }} />
              </>
            )}
          </div>

          <div className="ftcrep-side">
            <div className="ftcrep-panel">
              <h4>Recipients</h4>
              {recipients.length ? (
                recipients.map((r) => (
                  <div className="ftcrep-to" key={r.email}>
                    <span className="ftcrep-to-name">{r.name || r.email}</span>
                    {r.name ? <span className="ftcrep-to-mail">{r.email}</span> : null}
                  </div>
                ))
              ) : (
                <p className="ftcrep-note">
                  No contacts here are approved for you to email yet, so this report
                  can't be sent. An admin sets that on your assignment.
                </p>
              )}
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={sending}
                disabled={!preview || !recipients.length}
                onClick={confirmSend}
                style={{ marginTop: 12, width: "100%" }}
              >
                Send now
              </Button>
            </div>

            <div className="ftcrep-panel">
              <h4>Automatic sending</h4>
              <div className="ftcrep-row">
                <span>Send on a schedule</span>
                <Switch
                  checked={!!(schedule && schedule.enabled)}
                  onChange={(v) => patchSchedule({ enabled: v })}
                />
              </div>
              <div className="ftcrep-row">
                <span>Cadence</span>
                <Select
                  size="small"
                  value={(schedule && schedule.cadence) || "monthly"}
                  onChange={(v) => patchSchedule({ cadence: v })}
                  options={[
                    { value: "monthly", label: "Monthly" },
                    { value: "weekly", label: "Weekly" },
                  ]}
                  style={{ width: 120 }}
                />
              </div>
              {schedule && schedule.sender_email ? (
                <p className="ftcrep-note">Sent from {schedule.sender_email}</p>
              ) : (
                <p className="ftcrep-note">
                  Nobody is assigned to sign this clinic's reports, so none will go out.
                </p>
              )}
              {schedule && schedule.last_sent_at ? (
                <Tag className="ftcrep-lastsent">
                  Last sent {new Date(schedule.last_sent_at).toLocaleDateString()}
                </Tag>
              ) : null}
            </div>

            <div className="ftcrep-panel">
              <h4>Opening note</h4>
              <Input.TextArea
                value={(schedule && schedule.intro) || ""}
                onChange={(e) => setSchedule(Object.assign({}, schedule, { intro: e.target.value }))}
                onBlur={(e) => patchSchedule({ intro: e.target.value })}
                placeholder="An optional line above the figures…"
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
              <p className="ftcrep-note">Saved when you click away. Refresh to preview it.</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
